// The Action-type PAYLOAD renderers (brief §5.3.3): the EXACT frozen payload of the selected
// proposal, rendered natively per Action type — an email shown as an email, a state move as
// from → to, anything else (the credit-note shape) as key → value rows. Values are projection
// data rendered verbatim (P-SELF — never recomputed, never edited; editing is v1.1); tokens only
// (G-181); no internal identifier is invented here (G-197 — the payload is owner-authored data).

import type { IntentionView } from '../projections.ts';

/** Render one frozen payload value for display (strings verbatim; anything else stringified). */
function displayValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value === null || value === undefined) return '';
  return JSON.stringify(value);
}

/** An email payload rendered as an email — subject line, then the body (brief §5.3.3). */
function EmailPayload({
  payload,
}: {
  readonly payload: Readonly<Record<string, unknown>>;
}): React.JSX.Element {
  return (
    <div className="rounded-sm border border-border bg-surface-sunken px-3 py-2.5">
      <div className="ritona-voice text-sm font-medium text-text">
        {displayValue(payload['subject'])}
      </div>
      <p className="mt-1.5 text-sm leading-relaxed text-text-muted">
        {displayValue(payload['body'])}
      </p>
    </div>
  );
}

/** The generic key → value payload table (credit notes, CRM writes — brief §5.3.3). */
function KvPayload({
  payload,
}: {
  readonly payload: Readonly<Record<string, unknown>>;
}): React.JSX.Element {
  return (
    <div className="ritona-numeric grid grid-cols-[auto_1fr] gap-x-5 gap-y-1.5 text-xs">
      {Object.entries(payload).map(([key, value]) => (
        <div key={key} className="contents">
          <span className="text-text-subtle">{key}</span>
          <span className="text-text">{displayValue(value)}</span>
        </div>
      ))}
    </div>
  );
}

/** A state-move payload rendered as from → to; falls back to rows when the shape differs. */
function MovePayload({
  payload,
}: {
  readonly payload: Readonly<Record<string, unknown>>;
}): React.JSX.Element {
  const from = payload['from'];
  const to = payload['to'];
  if (typeof from === 'string' && typeof to === 'string') {
    return (
      <div className="ritona-numeric text-sm text-text">
        {from} <span className="text-text-subtle">→</span> {to}
      </div>
    );
  }
  return <KvPayload payload={payload} />;
}

/** The exact frozen payload of one proposal, rendered by its Action type's component. */
export function PayloadView({ option }: { readonly option: IntentionView }): React.JSX.Element {
  if (option.actionType === 'mail.send') return <EmailPayload payload={option.payload} />;
  if (option.actionType === 'state.move') return <MovePayload payload={option.payload} />;
  return <KvPayload payload={option.payload} />;
}
